import { useState, useEffect, useRef } from 'react';
import { Column } from "@tanstack/react-table"
import { XIcon, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DataTableFilterBase } from "@/components/ui/data-table-filter-base"

interface DataTableStringFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  title?: string
}

export function DataTableStringFilter<TData, TValue>({
  column,
  title,
}: DataTableStringFilterProps<TData, TValue>) {

  const [queries, setQueries] = useState<string[]>([""]);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const filterValue = column.getFilterValue();

  useEffect(() => {
    if (filterValue === undefined) {
      setQueries((current) => (
        current.length === 1 && current[0] === "" ? current : [""]
      ));
    } else if (Array.isArray(filterValue) && filterValue.length > 0) {
      setQueries((current) => (
        current.length === filterValue.length && current.every((value, index) => value === filterValue[index])
          ? current
          : filterValue
      ));
    }
  }, [filterValue]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const debouncedSetFilterValue = (value: string[]) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      column.setFilterValue(value);
    }, 300);
  };

  const handleAddFilter = () => {
    setQueries((prev) => [...prev, ""]);
  };

  const handleRemoveFilter = (index: number) => {
    setQueries((prev) => {
      const newQueries = [...prev].filter((_, i) => i !== index);;
      column.setFilterValue(newQueries);
      return newQueries;
    });
  };

  const handleInputChange = (index: number, value: string) => {
    setQueries((prev) => {
      const newQueries = [...prev];
      newQueries[index] = value;
      debouncedSetFilterValue(newQueries);
      return newQueries;
    });
  };

  const handleClearFilters = () => {
    setQueries([""]);
    column.setFilterValue(undefined);
  };

  const activeFilterCount = queries.filter((query) => query.trim() !== "").length;

  return (
    <DataTableFilterBase 
      filterCount={activeFilterCount}
      className="w-[250px] p-0"
      align="start"
    >
        {queries.map((query, index) => (
          <div key={index} className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              type="text"
              placeholder="Word or phrase..."
              value={query}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onPointerDown={(e) => {
                // Ensure focus works inside Dialog
                e.stopPropagation();
                (e.target as HTMLInputElement).focus();
              }}
              onClick={(e) => {
                e.stopPropagation();
                (e.target as HTMLInputElement).focus();
              }}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-stone-500 disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-stone-400"
            />
            {queries.length > 1 &&
              <Button
                variant={'ghost'}
                size={'sm'}
                onClick={() => handleRemoveFilter(index)}
              >
                <XIcon className='w-4 h-4'/>
              </Button>
            }
          </div>
        ))}
        <div className='flex flex-row gap-1 justify-end'>
        {queries.length > 0 && queries.filter((query) => query.trim() !== "").length > 0 && (
          <Button
            onClick={() => handleClearFilters()}
            variant={'outline'}
            size={'sm'}
            className='w-full'
          >
            Clear filters
          </Button>
        )}
          <Button 
            size={'sm'}
            onClick={handleAddFilter}
          >
            <span>Add</span>
          </Button>
        </div>
    </DataTableFilterBase>
  )
}
